import { useEffect, useRef, useState } from "react";
import {
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  PasswordInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import Request from "../common/Request.jsx";
import { publicCompany } from "../common/publicCompany";
import { RegistrationTracker } from "./RegistrationTracker";
import { OnboardingFooter, REGISTRATION_STEPS } from "./onboardingChrome";
import { ArrowRightIcon } from "./onboardingIcons";
import { termsOfServiceUrl } from "./v2/legalLinks";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PASSWORD_RULES: {
  id: string;
  label: string;
  test: (value: string) => boolean;
}[] = [
  { id: "length", label: "8+ characters", test: (v) => v.length >= 8 },
  {
    id: "alphaNum",
    label: "Letters and numbers",
    test: (v) => /[a-zA-Z]/.test(v) && /\d/.test(v),
  },
  {
    id: "mixedCase",
    label: "Upper and lowercase",
    test: (v) => /[a-z]/.test(v) && /[A-Z]/.test(v),
  },
  { id: "symbol", label: "A symbol", test: (v) => /[^a-zA-Z0-9]/.test(v) },
];

const SUBHEADING =
  "Free accounts have limited access to inventory management tools. To experience everything ArtCloud by Artlogic has to offer, you can sign up for a 14-day free trial after you create an account.";

const ERROR_MESSAGES = {
  invalidEmail: "Enter a valid email address.",
  invalidPassword: "Your password doesn't meet the requirements yet.",
  tcsRequired: "Please accept the Terms to continue.",
  duplicateEmail: "That email address is currently in use. Please sign in.",
  generic: "Something went wrong. Please try again.",
} as const;

const CONTROL_HEIGHT = 52;

export interface CreateAccountScreenProps {
  initialEmail?: string;
  isArtistReferral: boolean;
  onSuccess: (result: { sessionToken: string; email: string }) => void;
}

export function CreateAccountScreen({
  initialEmail = "",
  isArtistReferral,
  onSuccess,
}: CreateAccountScreenProps) {
  const emailRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [topLevelError, setTopLevelError] = useState<string | null>(null);

  const form = useForm({
    initialValues: { email: initialEmail, password: "", tcsAccepted: false },
    validate: {
      email: (value) =>
        EMAIL_REGEX.test(value) ? null : ERROR_MESSAGES.invalidEmail,
      password: (value) =>
        PASSWORD_RULES.every((rule) => rule.test(value))
          ? null
          : ERROR_MESSAGES.invalidPassword,
      tcsAccepted: (value) => (value ? null : ERROR_MESSAGES.tcsRequired),
    },
    validateInputOnBlur: ["email"],
  });

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const passwordValue = form.values.password;
  const allPasswordRulesPass = PASSWORD_RULES.every((rule) =>
    rule.test(passwordValue),
  );
  const emailPasses = EMAIL_REGEX.test(form.values.email);
  const canSubmit =
    emailPasses &&
    allPasswordRulesPass &&
    form.values.tcsAccepted &&
    !submitting;

  async function handleSubmit(values: typeof form.values) {
    setTopLevelError(null);
    setSubmitting(true);
    try {
      const url =
        (typeof window !== "undefined" &&
          (
            window as unknown as {
              Artcloud?: {
                url?: {
                  vendor?: {
                    onboarding?: {
                      registrations?: { account?: { post?: () => string } };
                    };
                  };
                };
              };
            }
          ).Artcloud?.url?.vendor?.onboarding?.registrations?.account?.post?.()) ||
        "/api/onboarding/registrations/account";
      const response = await Request.post(url, {
        Email: values.email,
        Password: values.password,
        TcsAccepted: values.tcsAccepted,
        IsArtistReferral: isArtistReferral,
      });
      if (response.status === 201) {
        onSuccess({ sessionToken: response.body?.Session ?? "", email: values.email });
        return;
      }
      if (response.status === 409) {
        form.setFieldError("email", ERROR_MESSAGES.duplicateEmail);
        return;
      }
      setTopLevelError(ERROR_MESSAGES.generic);
    } catch (_) {
      setTopLevelError(ERROR_MESSAGES.generic);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      style={{
        gridColumn: "2 / 3",
        gridRow: "1 / -1",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        component="nav"
        aria-label="Top navigation"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "0.35rem",
          padding: "1rem 1.5rem",
        }}
      >
        <Text size="sm" c="gray.7">
          Have an account?
        </Text>
        <Anchor
          href="/login"
          data-testid="sign-in-link"
          size="sm"
          fw={500}
        >
          Sign in
        </Anchor>
      </Box>

      <Box
        style={{
          maxWidth: "600px",
          margin: "0 auto",
          padding: "2rem 1.5rem",
        }}
      >
        <Box mb="lg">
          <RegistrationTracker steps={REGISTRATION_STEPS} activeStep={1} />
        </Box>

        <Stack gap="md" maw="480px" mx="auto" w="100%">
          <Title
            order={1}
            fz="40px"
            fw={600}
            lh={1.15}
            data-testid="screen-heading"
            style={{ letterSpacing: "-0.02em", margin: "8px 0 10px" }}
          >
            Try ArtCloud for free.
          </Title>
          <Text size="sm" c="gray.7" data-testid="screen-subheading">
            {SUBHEADING}
          </Text>

          <form
            aria-label="Create account"
            onSubmit={form.onSubmit(handleSubmit)}
            noValidate
          >
            <Stack gap="md">
              <TextInput
                label="Email address"
                type="email"
                placeholder="you@studio.com"
                autoComplete="email"
                required
                data-testid="email-input"
                ref={emailRef}
                styles={{
                  input: { height: CONTROL_HEIGHT, fontSize: 15 },
                  label: { fontWeight: 600, marginBottom: 6 },
                }}
                {...form.getInputProps("email")}
              />

              <Box>
                <PasswordInput
                  label="Password"
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  required
                  data-testid="password-input"
                  styles={{
                    input: { height: CONTROL_HEIGHT, fontSize: 15 },
                    label: { fontWeight: 600, marginBottom: 6 },
                  }}
                  {...form.getInputProps("password")}
                />
                <SimpleGrid
                  cols={2}
                  spacing="xl"
                  verticalSpacing="xs"
                  role="list"
                  aria-label="Password requirements"
                  mt="xs"
                  data-testid="password-rules"
                >
                  {PASSWORD_RULES.map((rule) => {
                    const passed = rule.test(passwordValue);
                    return (
                      <Group
                        key={rule.id}
                        role="listitem"
                        data-rule-id={rule.id}
                        data-rule-passed={passed ? "true" : "false"}
                        gap="xs"
                        wrap="nowrap"
                      >
                        <Badge
                          circle
                          size="sm"
                          variant={passed ? "filled" : "outline"}
                          color={passed ? "artcloud-blue" : "gray.4"}
                          aria-hidden="true"
                        >
                          {passed ? "✓" : ""}
                        </Badge>
                        <Text size="sm" c={passed ? "gray.8" : "gray.6"}>
                          {rule.label}
                        </Text>
                      </Group>
                    );
                  })}
                </SimpleGrid>
              </Box>

              <Checkbox
                data-testid="tcs-checkbox"
                label={
                  <Text span size="sm">
                    I agree to {publicCompany()}'s{" "}
                    <Anchor
                      href={termsOfServiceUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="sm"
                      inherit
                      data-testid="tcs-link"
                    >
                      Terms of Service
                    </Anchor>
                    .
                  </Text>
                }
                {...form.getInputProps("tcsAccepted", { type: "checkbox" })}
              />

              <Button
                type="submit"
                fullWidth
                h={CONTROL_HEIGHT}
                fw={500}
                disabled={!canSubmit}
                loading={submitting}
                rightSection={submitting ? undefined : <ArrowRightIcon />}
                data-testid="submit-button"
              >
                {submitting ? "Creating account…" : "Create account"}
              </Button>

              {topLevelError && (
                <Text
                  role="alert"
                  c="red.7"
                  size="sm"
                  data-testid="top-level-error"
                >
                  {topLevelError}
                </Text>
              )}
            </Stack>
          </form>
        </Stack>
      </Box>

      <OnboardingFooter />
    </Box>
  );
}
